import { useEffect, useState } from 'react'
import { LaunchDarkly } from '@truepill/tpos-types'
import { useUserInfo } from 'hooks'
import { useClient } from 'providers/LaunchDarklyProvider'
import { usePlusClient } from 'providers/VisionRouter'
import type { Customer, TPOSLocation } from 'types'

type UsePV1DesignV2Props = {
  customer?: Customer
  location?: TPOSLocation
}

/**
 * usePV1DesignV2 retrieves only the tposPv1DesignV2 feature flag from LaunchDarkly.
 *
 * @param customer - Prescription Customer object
 * @param location - Prescription Location object
 * @returns tposPv1DesignV2 boolean indicating if is enabled
 */
export function usePV1DesignV2({ customer, location }: UsePV1DesignV2Props): {
  tposPv1DesignV2: boolean
  loadingFeatureFlag: boolean
} {
  const defaultCustomerLegacyId = '0' // Truepill

  const { ldReady, ldIdentify } = useClient()
  const [tposPv1DesignV2, setTposPv1DesignV2] = useState(false)
  const [loadingFeatureFlag, setLoadingFeatureFlag] = useState(true)

  const {
    tokenContext: { id: userId },
  } = usePlusClient()
  const { userInfo } = useUserInfo(userId)

  useEffect(() => {
    if (!ldReady || !customer || !location || !userInfo) return

    const customerLegacyId = customer?.legacyId?.toString() ?? defaultCustomerLegacyId
    const locationLegacyId = location?.legacyId?.toString()
    const userLocationId = userInfo?.locationId?.toString()
    const username = userInfo?.username

    if (customerLegacyId && locationLegacyId && userLocationId && username) {
      ldIdentify(
        {
          key: customerLegacyId,
          email: userInfo?.email,
          custom: { locationLegacyId, username, userId, userLocationId },
        },
        undefined,
        (err, flags) => {
          if (flags) {
            setTposPv1DesignV2(flags[LaunchDarkly.FeatureFlags.TPOS_PV1_DESIGN_V2])
          }
          setLoadingFeatureFlag(false)
        },
      )
    } else {
      setLoadingFeatureFlag(false)
    }
  }, [ldReady, ldIdentify, userInfo, customer?.legacyId, userId, location?.legacyId])

  return { tposPv1DesignV2, loadingFeatureFlag }
}
